import React from 'react';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import AuthContextProvider from './contexts/AuthContext.jsx';
import PrivateRoute from './components/common/PrivateRoute.jsx';
import AppLayout from './components/layout/AppLayout.jsx';
import ResourcePage from './components/common/ResourcePage.jsx';
import { RESOURCES } from './config/resources.js';
import Login from './pages/auth/Login.jsx';
import Inicio from './pages/Inicio.jsx';
import ContenedoresListPage from './pages/contenedores/ContenedoresListPage.jsx';
import ContenedorFormPage from './pages/contenedores/ContenedorFormPage.jsx';
import GastosPage from './pages/finanzas-cxp/GastosPage.jsx';
import GastosContenedorPage from './pages/finanzas-cxp/GastosContenedorPage.jsx';
import PagosPage from './pages/finanzas-cxp/PagosPage.jsx';
import AnticiposProveedoresPage from './pages/finanzas-cxp/AnticiposProveedoresPage.jsx';
import SociosPage from './pages/socios-retiros/SociosPage.jsx';
import RetirosPage from './pages/socios-retiros/RetirosPage.jsx';
import PorRetirarPage from './pages/socios-retiros/PorRetirarPage.jsx';
import EstadoCuentaClientePage from './pages/reportes/EstadoCuentaClientePage.jsx';
import EstadoCuentaProveedorPage from './pages/reportes/EstadoCuentaProveedorPage.jsx';
import GraficasPage from './pages/reportes/GraficasPage.jsx';
import ReporteContenedoresPage from './pages/reportes/ReporteContenedoresPage.jsx';
import ReporteContenedoresCargosPage from './pages/reportes/ReporteContenedoresCargosPage.jsx';
import ReporteTransferenciasPage from './pages/reportes/ReporteTransferenciasPage.jsx';
import ReporteFacturasPage from './pages/reportes/ReporteFacturasPage.jsx';
import ReporteUtilidadPage from './pages/reportes/ReporteUtilidadPage.jsx';
import LogsPage from './pages/logs/LogsPage.jsx';
import CargosPage from './pages/finanzas/CargosPage.jsx';
import CargosContenedorPage from './pages/finanzas/CargosContenedorPage.jsx';
import ListadoContenedoresAdminPage from './pages/admin/ListadoContenedoresAdminPage.jsx';
import BuscarContenedorPage from './pages/admin/BuscarContenedorPage.jsx';
import AbonosPage from './pages/finanzas/AbonosPage.jsx';
import AbonosCapturaPage from './pages/finanzas/AbonosCapturaPage.jsx';
import AnticiposPage from './pages/finanzas/AnticiposPage.jsx';
import BancosPage from './pages/finanzas/BancosPage.jsx';
import BancosTraspasosPage from './pages/finanzas/BancosTraspasosPage.jsx';
import MovimientosBancosPage from './pages/finanzas/MovimientosBancosPage.jsx';
import DevolucionesPage from './pages/finanzas/DevolucionesPage.jsx';
import DevolucionesInversaPage from './pages/finanzas/DevolucionesInversaPage.jsx';
import BonificacionesProveedoresPage from './pages/finanzas/BonificacionesProveedoresPage.jsx';
import AsistenteIAPage from './pages/asistente-ia/AsistenteIAPage.jsx';
import WhatsAppPanelPage from './pages/whatsapp/WhatsAppPanelPage.jsx';

/**
 * Rutas de dominio: cada página se agrega aquí conforme se construye (ver
 * PLAN.md Fase 11). El layout (`AppLayout`) ya resuelve Sidebar+Header, así que las
 * páginas de negocio solo se registran como hijas de la ruta raíz protegida.
 */
// V1 tiene 4 pantallas distintas sobre el modelo `Proveedores` (Proveedores, Navieras,
// Agentes, Forwarders); en V2 comparten la ruta `/proveedores` y se distinguen con
// `?tipo=Naviera|Agente|Forwarder` (links del Sidebar).
function ProveedoresPorTipo() {
  const [params] = useSearchParams();
  const tipo = params.get('tipo');
  const cfg = tipo === 'Naviera' ? RESOURCES.navieras
    : tipo === 'Agente' ? RESOURCES.agentes
    : tipo === 'Forwarder' ? RESOURCES.forwarders
    : RESOURCES.proveedores;
  return <ResourcePage key={cfg.title} cfg={cfg} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthContextProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Inicio />} />

            {/* Catálogos simples y entidades principales (config-driven, ver
                config/resources.js) — Fases 1/2/3 ya tienen backend listo. */}
            <Route path="articulos" element={<ResourcePage cfg={RESOURCES.articulos} />} />
            <Route path="empresas" element={<ResourcePage cfg={RESOURCES.empresas} />} />
            <Route path="terminales" element={<ResourcePage cfg={RESOURCES.terminales} />} />
            <Route path="programas" element={<ResourcePage cfg={RESOURCES.programas} />} />
            <Route path="patentes" element={<ResourcePage cfg={RESOURCES.patentes} />} />
            <Route path="conceptos-gastos" element={<ResourcePage cfg={RESOURCES['conceptos-gastos']} />} />
            <Route path="clientes" element={<ResourcePage cfg={RESOURCES.clientes} />} />
            <Route path="proveedores" element={<ProveedoresPorTipo />} />
            <Route path="usuarios" element={<ResourcePage cfg={RESOURCES.usuarios} />} />

            {/* Contenedores — objeto central del negocio (~50 campos), página bespoke
                (no ResourcePage genérico: ver ContenedoresListPage/ContenedorFormPage). */}
            <Route path="contenedores" element={<ContenedoresListPage />} />
            <Route path="contenedores/nuevo" element={<ContenedorFormPage />} />
            <Route path="contenedores/:id" element={<ContenedorFormPage />} />

            {/* Finanzas CxP — Fase 11 */}
            <Route path="gastos" element={<GastosPage />} />
            <Route path="gastos-contenedor" element={<GastosContenedorPage />} />
            <Route path="pagos" element={<PagosPage />} />
            <Route path="anticipos-proveedores" element={<AnticiposProveedoresPage />} />

            {/* Socios / Retiros / PorRetirar — Fase 11 */}
            <Route path="socios" element={<SociosPage />} />
            <Route path="retiros" element={<RetirosPage />} />
            <Route path="por-retirar" element={<PorRetirarPage />} />

            {/* Reportes — Fase 11 */}
            <Route path="reportes/estado-cuenta/clientes" element={<EstadoCuentaClientePage />} />
            <Route path="reportes/estado-cuenta/proveedores" element={<EstadoCuentaProveedorPage />} />
            <Route path="reportes/graficas" element={<GraficasPage />} />
            <Route path="reportes/contenedores" element={<ReporteContenedoresPage />} />
            <Route path="reportes/contenedores-cargos" element={<ReporteContenedoresCargosPage />} />
            {/* Reportes faltantes portados en la auditoría 2026-09-28 (ver DUDAS.md) --
                V1: Reportes/{ReporteTransferencias,ReporteFacturas,ReporteUtilidades}.jsx */}
            <Route path="reportes/transferencias" element={<ReporteTransferenciasPage />} />
            <Route path="reportes/facturas" element={<ReporteFacturasPage />} />
            <Route path="reportes/utilidad" element={<ReporteUtilidadPage />} />
            <Route path="logs" element={<LogsPage />} />

            {/* Finanzas CxC — Fase 11 */}
            <Route path="admin/contenedores" element={<ListadoContenedoresAdminPage />} />
            <Route path="admin/buscar-contenedor" element={<BuscarContenedorPage />} />
            <Route path="cargos" element={<CargosPage />} />
            <Route path="cargos-contenedor" element={<CargosContenedorPage />} />
            <Route path="abonos" element={<AbonosPage />} />
            <Route path="abonos/nuevo" element={<AbonosCapturaPage />} />
            <Route path="anticipos" element={<AnticiposPage />} />
            <Route path="bancos" element={<BancosPage />} />
            <Route path="bancos-traspasos" element={<BancosTraspasosPage />} />
            <Route path="movimientos-bancos" element={<MovimientosBancosPage />} />
            <Route path="devoluciones" element={<DevolucionesPage />} />
            <Route path="devoluciones-inversa" element={<DevolucionesInversaPage />} />
            <Route path="bonificaciones-proveedores" element={<BonificacionesProveedoresPage />} />

            {/* Asistente IA — Fase 11 (además del widget flotante en AppLayout.jsx) */}
            <Route path="asistente-ia" element={<AsistenteIAPage />} />

            {/* WhatsApp — una sola pantalla como V1 (panel + Telefonos Registrados), solo-admin, ver DUDAS.md D-11.13 */}
            <Route path="whatsapp" element={<WhatsAppPanelPage />} />
          </Route>
        </Routes>
      </AuthContextProvider>
    </BrowserRouter>
  );
}
